// @lumal/lib — utilidades compartidas entre apps.

export * from './validation.js';

export interface ErrorResponse {
  code: string;
  message_safe: string;
  request_id: string;
}

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | { [k: string]: JsonValue }
  | JsonValue[];

export interface FetchOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  body?: unknown;
}

/**
 * apiFetch envuelve fetch con credentials por default, propagación de
 * X-Request-Id si está en el contexto, timeouts via AbortSignal.
 * Devuelve JSON parseado o lanza el ErrorResponse del BFF.
 */
export async function apiFetch<T = unknown>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
  opts: FetchOptions = {},
): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: 'include',
    signal: opts.signal,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers ?? {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : (undefined as unknown);
  if (!res.ok) {
    throw (json ?? {
      code: 'INTERNAL',
      message_safe: `HTTP ${res.status}`,
      request_id: res.headers.get('X-Request-Id') ?? '',
    }) as ErrorResponse;
  }
  return json as T;
}

export interface LogoutResponse {
  logged_out: boolean;
  keycloak_logout_url?: string;
  public_origin?: string;
}

/**
 * logout cierra la sesión local (POST /auth/logout) y luego NAVEGA el navegador
 * al RP-initiated logout de Keycloak (keycloak_logout_url) para terminar la cookie
 * SSO; Keycloak redirige de vuelta al post_logout_redirect_uri (el front).
 *
 * Sin esta navegación la sesión SSO de Keycloak sobrevive y el siguiente login no
 * pide contraseña. Si keycloak_logout_url viene vacío (provider sin
 * end_session_endpoint), no navega y el caller cae a su comportamiento de pre-login
 * (p. ej. invalidar la query ['me']).
 */
export async function logout(apiBase: string, csrfToken: string): Promise<LogoutResponse> {
  const res = await apiFetch<LogoutResponse>(`${apiBase}/auth/logout`, 'POST', {
    headers: { 'X-CSRF-Token': csrfToken },
  });
  if (res.keycloak_logout_url) {
    window.location.href = res.keycloak_logout_url;
  }
  return res;
}
